/**
 * useAccountConfirmation Hook
 * Specialized hook for account confirmation flow
 */

import { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { confirmSupabaseAccount } from '../services/authService';
import { ANIMATION_DELAYS } from '../utils/appConstants';
import { useAuthContext } from '../contexts/AuthContext';

/**
 * Custom hook for handling account confirmation process
 * @returns {Object} Confirmation state and handlers
 */
export const useAccountConfirmation = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login } = useAuthContext();
  const [status, setStatus] = useState('loading'); // 'loading', 'success', 'error'
  const [message, setMessage] = useState('');

  // Prevent duplicate verification attempts
  const verificationAttempted = useRef(false);
  const mounted = useRef(true);
  const abortController = useRef(null);

  useEffect(() => {
    mounted.current = true;

    return () => {
      mounted.current = false;
      if (abortController.current) {
        abortController.current.abort();
      }
    };
  }, []);

  useEffect(() => {
    // Prevent multiple verification attempts
    if (verificationAttempted.current) {
      return;
    }

    const verifyAccount = async () => {
      verificationAttempted.current = true;

      try {
        const supabaseError = searchParams.get('error_description') || searchParams.get('error');

        if (supabaseError) {
          if (mounted.current) {
            setStatus('error');
            setMessage(decodeURIComponent(supabaseError).replace(/\+/g, ' '));
          }
          return;
        }

        abortController.current = new AbortController();
        const response = await confirmSupabaseAccount();

        if (response.ok && !response.data.error) {
          if (mounted.current) {
            setStatus('success');
            setMessage(response.data.message || 'Je account is succesvol bevestigd!');

            // Auto-login using AuthContext if tokens are provided
            if (response.data.accessToken && response.data.gebruiker) {
              login(
                response.data.accessToken,
                response.data.gebruiker,
                response.data.refreshToken
              );
            }

            // Determine redirect based on account type
            const accountType = response.data.gebruiker?.accountType;
            const redirectPath = accountType === 'student'
              ? '/dashboard?tab=sollicitaties'
              : accountType === 'werkgever'
                ? '/dashboard'
                : '/inloggen';

            // Redirect after delay
            setTimeout(() => {
              navigate(redirectPath);
            }, ANIMATION_DELAYS.REDIRECT);
          }
        } else {
          if (mounted.current) {
            setStatus('error');
            const errorMessage = response.data.error ||
              'Er ging iets mis bij het bevestigen van je account.';
            setMessage(errorMessage);
          }
        }
      } catch (error) {
        if (error.name === 'AbortError') {
          // Request was aborted, likely due to component unmount
          verificationAttempted.current = false;
          return;
        }

        if (mounted.current) {
          setStatus('error');
          setMessage(
            error.message.includes('Failed to fetch')
              ? 'Kan geen verbinding maken met de server. Controleer je internetverbinding en probeer het opnieuw.'
              : 'Er ging iets mis bij het bevestigen van je account.'
          );
        }
      }
    };

    // Small delay to prevent immediate abort in React Strict Mode
    const timer = setTimeout(() => {
      if (mounted.current) {
        verifyAccount();
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [searchParams, navigate, login]);

  return {
    status,
    message,
    isLoading: status === 'loading',
    isSuccess: status === 'success',
    isError: status === 'error',
  };
};

export default useAccountConfirmation;
